// app/javascript/components/modals/DoNotContactModal.tsx
import * as React from "react";
import {
  Box,
  Typography,
  IconButton,
} from "@mui/material";

// Icon Imports
import DeleteIcon from "@mui/icons-material/Delete";

// Utility and Context Imports
import { getHeaders } from "../utils/HeaderHelper";
import { AuthenticationContext } from "../Context";

// Component Imports
import GeneralModal from "./GeneralModal"; 
import DatePickerWithLabel from "../common/DatePickerWithLabel"; 
import moment from "moment/moment";
import { useFlash } from "../FlashContext";

interface Props {
  open: boolean;
  onClose: () => void;
  patientId: number | string;
  initialDncDate: string | null; 
  // Updated callback to pass the specific action type to PatientInfo
  onSaveSuccess: (newDate: string | null, actionType: "added" | "updated" | "removed") => void;
}

const DoNotContactModal: React.FC<Props> = ({
  open,
  onClose,
  patientId,
  initialDncDate,
  onSaveSuccess,
}) => {
  const authenticationSetting = React.useContext(AuthenticationContext);
  const { showToast } = useFlash();

  const [selectedDate, setSelectedDate] = React.useState<string>("");
  const [loading, setLoading] = React.useState<boolean>(false);
  const [validationError, setValidationError] = React.useState<string>("");

  React.useEffect(() => {
    if (open) {
      setSelectedDate(initialDncDate || "");
      setValidationError(""); // Clear any previous errors on open
    }
  }, [open, initialDncDate]);

  const handleClose = () => {
    setSelectedDate(initialDncDate || "");
    setValidationError("");
    onClose();
  };

  const handleClearDate = () => {
    setSelectedDate("");
    setValidationError(""); // Clearing the date removes the error state
  };

  const handleSave = async () => {
    setValidationError(""); // Reset validation

    // 1. Validation Check (Prevents manual entry of past/invalid dates)
    if (selectedDate !== "") {
      const isPast = moment(selectedDate, "YYYY-MM-DD").startOf("day").isBefore(moment().startOf("day"));
      const isValid = moment(selectedDate, "YYYY-MM-DD", true).isValid();

      if (!isValid || isPast) {
        setValidationError("Please select a valid future date for Do Not Contact.");
        return; // System prevents save
      }
    }

    // 2. Determine the Action Type for the Toast Message
    let actionType: "added" | "updated" | "removed" = "added";
    if (initialDncDate && selectedDate !== "") {
      actionType = "updated";
    } else if (initialDncDate && selectedDate === "") {
      actionType = "removed";
    }

    // 3. API Execution
    setLoading(true);
    try {
      const payload = {
        patient: {
          do_not_contact_until: selectedDate === "" ? null : selectedDate,
        },
      };

      const response = await fetch(`/patients/${patientId}/update_dnc`, {
        method: "PATCH",
        headers: {
          ...getHeaders(authenticationSetting.csrfToken),
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.success) {
        onSaveSuccess(selectedDate === "" ? null : selectedDate, actionType);
        onClose();
      } else {
        console.error("Failed to update DNC date:", result.errors);
        // Display Error Toast
        showToast({
          title: "Error",
          text: "Do Not Contact settings could not be saved. Please try again.",
          type: "error",
        });
      }
    } catch (err) {
      console.error("Network error while saving DNC date:", err);
      // Display Error Toast
      showToast({
        title: "Error",
        text: "Do Not Contact settings could not be saved. Please try again.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Dynamic Copy Logic from Figma Design
  const hasExistingDate = !!initialDncDate;
  const isRemoving = hasExistingDate && selectedDate === "";

  let instructions =
    "This patient is currently available for contact. Set a date if the patient should not be contacted until a specific time.";
  if (hasExistingDate && !isRemoving) {
    instructions =
      "This patient should not be contacted until the selected date. You can update or remove this date at any time.";
  } else if (isRemoving) {
    instructions =
      "You are about to remove the Do Not Contact date. Communications will resume.";
  }

  return (
    <GeneralModal
      open={open}
      title={<span style={{ fontWeight: 600 }}>Do Not Contact Settings</span>}
      closeCallback={handleClose}
      successCallback={handleSave}
      confirmButtonText="Save"
      cancelButtonText="Cancel"
      isLoading={loading}
      width="500px"
      showClosedIcon={true}
      headerClassName="dnc-modal-header"
    >
      <Box sx={{ pt: 1, pb: 1 }}>

        <Typography color="#666666" sx={{ mb: 2, fontSize: "15px" }}>
          {instructions}
        </Typography>

        <Box display="flex" alignItems="flex-end" gap={2}>
          <Box
            sx={{
              flexGrow: 1,
              borderBottom: validationError ? "2px solid #D32F2F" : "1px solid #CCCCCC", // Red border on error
              transition: "border-bottom-color 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
              "&:focus-within": {
                borderBottom: validationError ? "2px solid #D32F2F" : "2px solid #FF890A",
                marginBottom: "-1px", 
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: validationError ? "#D32F2F" : "#FF890A", // Red label on error
              },
              "& .MuiInputBase-input": {
                paddingBottom: "8px", 
                color: "#212121",
              },
              "& .MuiSvgIcon-root": {
                fontSize: "26px", 
                color: validationError ? "#D32F2F" : "#868382", // Red icon on error
                cursor: "pointer",
                opacity: 0.7,
                transform: "translateY(-4px)", 
                "&:hover": { opacity: 1, color: "#333333" },
              },
              "& .date-container": {
                margin: 0,
              },
              "& .MuiInputBase-root": {
                marginTop: "28px",
              }
            }}
          >
            <DatePickerWithLabel
              date={selectedDate}
              setDate={(newDate: string) => {
                 setSelectedDate(newDate);
                 setValidationError(""); // Clear error when user types
              }}
              initialDate={initialDncDate || ""}
              label="Do Not Contact Until"
              disablePast={true} // UI level block
              background="transparent"
              height="auto"
              fontSize={14}
              labelFontSize={20}
              labelColor="#1E1E1E"
              labelMarginTop="3px"
            />
          </Box>

          {hasExistingDate && (
            <IconButton
              onClick={handleClearDate}
              disabled={loading || selectedDate === ""}
              title="Remove DNC Date"
              sx={{
                color: "#868382",
                opacity: 0.7,
                mt: "auto",
                mb: "-6px",
                "&:hover": { opacity: 1, color: "#333333" },
              }}
            >
              <DeleteIcon sx={{ fontSize: "26px" }} />
            </IconButton>
          )}
        </Box>

        {/* Validation Messaging UI */}
        {validationError && (
          <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1, fontWeight: 500 }}>
            {validationError}
          </Typography>
        )}
      </Box>
    </GeneralModal>
  );
};

export default DoNotContactModal;